import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Picture from '@deporvillage/picture-component';
import { I18nContext } from '@deporvillage/utils-library-component';

import styles from './NavigationBar.module.scss';

const Type = {
    Default: 'default',
    Rift: 'rift',
};

const DepthLevel = {
    Level_0: 0,
    Level_1: 1,
    Level_2: 2,
};

const NavigationLogo = ({ name, url, image }) => (
    <a className={styles['navigation-logo-component']} href={url} title={name}>
        <Picture src={`brand/logo/bw/${image}`} height={70} width={70} name={name} lazy />
    </a>
);
NavigationLogo.propTypes = {
    name: PropTypes.string,
    url: PropTypes.string,
    image: PropTypes.string,
};

const NavigationItem = ({ id, name, url, onClick }) => (
    <a
        className={styles['navigation-item-component']}
        href={url || null}
        title={name}
        onClick={() => onClick && onClick(url || null)}
        data-testid={id ? `a-scat-${id}` : null}
    >
        {name}
    </a>
);
NavigationItem.propTypes = {
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    url: PropTypes.string,
    onClick: PropTypes.func,
};

const NavigationDropdown = ({ items, onClick, logos, url }) => {

    const { translate } = React.useContext(I18nContext);

    let parts = url.replace(/^\//, '');
    let brandsUrl = `/${translate('header.title.brands')}-${parts}`;

    let columns = items.reduce((p, n) => {
        if (!p[n.column]) p[n.column] = [];
        p[n.column].push(n);
        return p;
    }, []);

    return (
        <div className={styles['navigation-dropdown-component']}>
            <div className={styles['dropdown-wrapper']}>
                <div className={styles['dropdown-columns-wrapper']}>
                    {columns.map((column, idx) => (
                        <div key={idx} className={styles['dropdown-column']}>
                            {column.map((item) => (
                                <div key={`${item.url}-${item.name}`} className={styles['dropdown-group']}>
                                    <NavigationItem
                                        {...item}
                                        onClick={(url) => onClick(url, DepthLevel.Level_1)}
                                    />

                                    {item.items.map((item) => (
                                        <NavigationItem
                                            key={`${item.url}-${item.name}`}
                                            {...item}
                                            onClick={(url) => onClick(url, DepthLevel.Level_2)}
                                        />
                                    ))}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                {logos && logos.length ? (
                    <div className={styles['dropdown-logos-wrapper']}>
                        {logos.map((logo) => (
                            <NavigationLogo key={logo.url} {...logo} />
                        ))}
                        <a className={styles['brands-more']} data-testid='brands-more' href={brandsUrl}>
                            <span className='icon icon-more' />
                        </a>
                    </div>
                ) : null}
            </div>
        </div>
    );
};
NavigationDropdown.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            name: PropTypes.string,
            url: PropTypes.string,
        }),
    ),
    onClick: PropTypes.func,
    logos: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            url: PropTypes.string,
            image: PropTypes.string,
        }), 
    ),
    url: PropTypes.string,
};

const NavigationBar = ({ items, onClick }) => {

    const [visibleIdx, setVisibleIdx] = React.useState(null);
    const handleOnMouseEnter = (idx) => setVisibleIdx(idx);

    const getTypeClassName = (type) => {
        switch (type) {
            case Type.Rift:
                return styles.rift;    
            default:
                return '';
        }
    };
    
    return (
        !items ? null : (
            <div className={styles['navigation-bar-component']}>
                {items.map(({ id, name, badge, type, url, isActive, items, weight, logos }, idx) =>
                    <Fragment key={`${url}-${name}`}>
                        <a
                            className={`${isActive ? styles['active'] : ''} ${ /* istanbul ignore next */ weight ?styles['shrink'] : ''}`}
                            data-testid={id ? `a-mcat-${id}` : null}
                            onClick={() => onClick && onClick(url, DepthLevel.Level_0)}
                            onMouseEnter={() => handleOnMouseEnter(idx)}
                            href={url}
                        >
                            <span className={getTypeClassName(type)}>
                                {name}
                            </span>
                            {
                                badge && (
                                    <span className={styles.badge}>
                                        {badge}
                                    </span>
                                )
                            }
                        </a>

                        {(items && (idx === visibleIdx || isActive)) && (
                            <NavigationDropdown items={items}
                                logos={logos}
                                url={url}
                                onClick={(url, l) => onClick && onClick(url, l)} />
                        )}
                    </Fragment>
                )}
            </div>
        )
    );
};
NavigationBar.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            name: PropTypes.string,
            url: PropTypes.string,
            isActive: PropTypes.bool,
            badge: PropTypes.string,
            type: PropTypes.oneOf([
                Type.Default,
                Type.Rift,
            ]),
        }),
    ),
    onClick: PropTypes.func
};

export default NavigationBar;
